/**
 * ApprovalAIChat — Inline floating AI chat card for approval editing
 *
 * Appears as a compact chat card on the approval detail page.
 * User can converse with AI to modify approval content.
 * Supports: field-specific editing, general questions, content regeneration.
 *
 * Used by: Selection toolbar "Chat" action, "Regenerate with AI" button
 */

import { useState, useRef, useEffect } from 'react';
import { Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import { ArgusEye } from '@/components/icons';
import { cn } from '@/lib/utils';

interface ChatMessage {
  id: string;
  role: 'user' | 'argus';
  content: string;
}

interface ApprovalAIChatProps {
  open: boolean;
  onClose: () => void;
  /** Approval being discussed — used by the streaming AI-assist backend
   *  (approval-ai-assist.service). Accepted now; full SSE wiring is the
   *  remaining slice of approvals#5 (backend is built). */
  approvalId?: string;
  /** Initial context — what the user selected or what field they're editing */
  context?: string;
  /** Field being edited (for targeted updates) */
  fieldName?: string;
  /** Called when AI suggests an update to apply */
  onApplyUpdate?: (fieldName: string, newValue: string) => void;
  className?: string;
}

let _chatId = 0;

// Bug #1105 — the placeholder accepted user input and replied with a
// canned not-wired refusal. That is exactly the "mock/fake interactive
// path on an approval decision surface" pattern the issue calls out:
// it LOOKS like a real AI assistant, accepts a question, and then
// returns text that did not analyze the approval. The issue's
// expected behavior is "the chat is disabled/clearly marked
// unavailable BEFORE accepting input".
//
// Until the approval-context LLM endpoint lands we render the panel
// as a read-only honest placeholder: the input + send button are
// disabled, the user cannot type, and the greeting explains where
// the real reasoning data lives. No more fake interactive path.
const APPROVAL_AI_CHAT_AVAILABLE = false;

export function ApprovalAIChat({ open, onClose, context, fieldName, onApplyUpdate, className }: ApprovalAIChatProps) {
  // onApplyUpdate is intentionally accepted but unused while the AI
  // chat is a placeholder (see handleSend below) — the prop stays so
  // we don't break callers when the real backend lands.
  void onApplyUpdate;
  void context;
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      // AI review is not yet wired to a real backend with approval
      // context. Surface that honestly instead of pretending to
      // analyze the approval (bug #108 + #1105).
      const greeting = `**AI review is not yet implemented for approvals.**\n\nThis chat panel is read-only until the approval-context LLM endpoint lands. Once available it will answer questions about:\n\n- Why the agent chose this action\n- Whether the cited evidence supports the recommendation\n- The risk score and what drove it\n- Suggested edits${fieldName ? ` (currently focused on the **${fieldName}** field)` : ''}\n\nFor now, use the **AI Decision Reasoning**, **Alternatives Considered**, and **Sources Cited** sections on the left to review this approval.`;
      setMessages([{ id: `ac-${++_chatId}`, role: 'argus', content: greeting }]);
    }
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  // Reply handler — bug #1105 hardening on top of fix #108.
  //
  // The chat is unavailable until the approval-context LLM endpoint
  // lands. APPROVAL_AI_CHAT_AVAILABLE is hardcoded false, so this
  // handler is a no-op — the input + send button are also disabled
  // below so a user cannot reach this path through the UI.
  //
  // The defensive early-return stays in place so a future regression
  // that re-enables the input (without wiring a real backend) cannot
  // accidentally produce a canned reply. When the real endpoint
  // lands, replace this body with the call to the LLM service.
  const handleSend = () => {
    if (!APPROVAL_AI_CHAT_AVAILABLE) return;
    const text = input.trim();
    if (!text || isTyping) return;
    setMessages(prev => [...prev, { id: `ac-${++_chatId}`, role: 'user', content: text }]);
    setInput('');
    setIsTyping(true);
    // No simulated reply. The real endpoint will populate the
    // response when the flag flips to true.
  };

  if (!open) return null;

  return (
    <div className={cn(
      'border rounded-xl bg-card shadow-lg overflow-hidden flex flex-col',
      'w-full max-w-md max-h-[400px]',
      className
    )}>
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30 shrink-0">
        <ArgusEye state="idle" className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold flex-1">Argus AI Assistant</span>
        {fieldName && <Badge className="text-[8px]">{fieldName}</Badge>}
        <Button variant="ghost" size="icon" className="h-6 w-6" title="Close" onClick={onClose}>
          <X className="h-3 w-3" />
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-auto p-3 space-y-2.5 min-h-[120px]">
        {messages.map(msg => (
          msg.role === 'argus' ? (
            <div key={msg.id} className="flex gap-2">
              <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <ArgusEye state="idle" className="h-3.5 w-3.5 text-primary" />
              </div>
              <div className="bg-muted/40 rounded-xl rounded-tl-sm px-3 py-2 max-w-[85%]">
                <div className="text-xs">
                  <MarkdownRenderer content={msg.content} />
                </div>
              </div>
            </div>
          ) : (
            <div key={msg.id} className="flex justify-end">
              <div className="bg-primary text-primary-foreground rounded-xl rounded-tr-sm px-3 py-2 max-w-[85%] text-xs">
                {msg.content}
              </div>
            </div>
          )
        ))}
        {isTyping && (
          <div className="flex gap-2">
            <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <ArgusEye state="thinking" blink="fast" className="h-3.5 w-3.5 text-primary" />
            </div>
            <div className="bg-muted/40 rounded-xl rounded-tl-sm px-3 py-2">
              <div className="flex items-center gap-1">
                <span className="h-1 w-1 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="h-1 w-1 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="h-1 w-1 rounded-full bg-primary/50 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input — Bug #1105: input + send are disabled until the
          approval-context LLM endpoint lands. The placeholder text
          tells the user the panel is read-only so they don't try to
          type a question and expect an answer. */}
      <div className="p-2 border-t flex gap-1.5 shrink-0">
        <Input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSend(); } }}
          placeholder={
            APPROVAL_AI_CHAT_AVAILABLE
              ? 'Ask or describe changes...'
              : 'AI review unavailable — coming soon'
          }
          className="h-8 text-xs"
          disabled={!APPROVAL_AI_CHAT_AVAILABLE || isTyping}
          aria-disabled={!APPROVAL_AI_CHAT_AVAILABLE}
        />
        <Button
          onClick={handleSend}
          disabled={!APPROVAL_AI_CHAT_AVAILABLE || !input.trim() || isTyping}
          aria-disabled={!APPROVAL_AI_CHAT_AVAILABLE}
          size="icon"
          className="h-8 w-8 shrink-0 rounded-lg"
          aria-label="Send message"
          title={
            APPROVAL_AI_CHAT_AVAILABLE
              ? 'Send'
              : 'AI review for approvals is not yet available'
          }
        >
          <Send className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// Badge component used locally (avoid import cycle)
function Badge({ className, children }: { className?: string; children: React.ReactNode }) {
  return <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 font-semibold', className)}>{children}</span>;
}
